import React, { useState, useEffect } from "react";
import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  ShieldAlert,
  ChevronDown,
  Eye,
  Users,
  Clock,
  ImageOff,
  Camera,
} from "lucide-react";
import { Modal, Select } from "antd";
import axios from '../../utils/axiosDash';

const { Option } = Select;

const CROWD_THRESHOLD_CRITICAL = 6;
const CROWD_THRESHOLD_WARNING  = 4;

function deriveLevel(item) {
  if (item.zoneType === "Head Count") {
    const personCount = item.details?.total_persons || 0;
    if (personCount >= CROWD_THRESHOLD_CRITICAL) return "critical";
    if (personCount >= CROWD_THRESHOLD_WARNING) return "warning";
    return "ok";
  }
  if (item.zoneType === "Intrusion") return "critical";
  if (item.zoneType === "Tailgating") return "warning";
  return "ok";
}

function isoToTime(isoStr) {
  if (!isoStr) return "—";
  const str = typeof isoStr === "object" ? isoStr?.toISOString?.() ?? "" : String(isoStr);
  const t = str.split("T")[1];
  return t ? t.substring(0, 8) : "—";
}

function isoToDate(isoStr) {
  if (!isoStr) return "—";
  const str = typeof isoStr === "object" ? isoStr?.toISOString?.() ?? "" : String(isoStr);
  const d = str.split("T")[0];
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
}

const levelCfg = {
  critical: {
    bg: "bg-red-50",
    border: "border-red-300",
    leftBar: "bg-red-500",
    text: "text-red-500",
    pillBg: "bg-red-100",
    label: "Critical",
    Icon: AlertOctagon,
  },
  warning: {
    bg: "bg-amber-50",
    border: "border-amber-300",
    leftBar: "bg-amber-500",
    text: "text-amber-500",
    pillBg: "bg-amber-100",
    label: "Warning",
    Icon: AlertTriangle,
  },
  ok: {
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    leftBar: "bg-emerald-400",
    text: "text-emerald-600",
    pillBg: "bg-emerald-100",
    label: "OK",
    Icon: CheckCircle2,
  },
};

const Label = ({ children, className = "" }) => (
  <span className={`text-[10px] font-bold uppercase tracking-[0.8px] text-slate-400 ${className}`}>
    {children}
  </span>
);

/* ── Thumbnail ── */
const AlertThumbnail = ({ url, cam, onClick }) => {
  const [status, setStatus] = useState("loading");

  return (
    <button
      onClick={onClick}
      title="View full snapshot"
      style={{ width: 72, height: 52, flexShrink: 0 }}
      className="group relative overflow-hidden rounded-lg border border-slate-200 bg-slate-100 transition-all duration-150 hover:border-violet-400 hover:shadow-lg hover:shadow-violet-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
    >
      {/* Shimmer */}
      {status === "loading" && (
        <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200" />
      )}

      {/* Error */}
      {status === "error" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-100 gap-0.5">
          <ImageOff size={13} className="text-slate-400" />
          <span className="text-[8px] font-medium text-slate-400">No img</span>
        </div>
      )}

      {/* Image */}
      {status !== "error" && (
        <img
          src={url}
          alt={`Snapshot – ${cam}`}
          className={`h-full w-full object-cover transition-transform duration-200 group-hover:scale-110 ${
            status === "loaded" ? "opacity-100" : "opacity-0"
          }`}
          onLoad={() => setStatus("loaded")}
          onError={() => setStatus("error")}
        />
      )}

      {/* Hover veil */}
      {status === "loaded" && (
        <div className="absolute inset-0 flex items-center justify-center bg-violet-900/0 transition-all duration-150 group-hover:bg-violet-900/40">
          <div className="flex items-center gap-1 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
            <Eye size={12} className="text-white drop-shadow" />
            <span className="text-[9px] font-bold text-white drop-shadow">View</span>
          </div>
        </div>
      )}
    </button>
  );
};

/* ── No-image placeholder same size as thumbnail ── */
const NoImagePlaceholder = () => (
  <div
    style={{ width: 72, height: 52, flexShrink: 0 }}
    className="flex flex-col items-center justify-center gap-0.5 rounded-lg border border-dashed border-slate-200 bg-slate-50"
  >
    <Camera size={13} className="text-slate-300" />
    <span className="text-[8px] font-medium text-slate-300">No image</span>
  </div>
);

/* ══════════════════════════════════════════════════════ */
const Alerts = () => {
  const [levelFilter,  setLevelFilter]  = useState("all");
  const [camFilter,    setCamFilter]    = useState("All");
  const [moduleFilter, setModuleFilter] = useState("All");
  const [dateFilter,   setDateFilter]   = useState("");
  const [expanded,     setExpanded]     = useState(null);
  const [previewAlert, setPreviewAlert] = useState(null);
  const [alerts,       setAlerts]       = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [imageLoading, setImageLoading] = useState(false);

  const fetchTablespace = async () => {
    try {
      setLoading(true);
      const today = new Date();
      const twoDaysAgo = new Date();
      twoDaysAgo.setDate(today.getDate() - 2);

      const response = await axios.post("/fetch-for-alerts-reports-tablespace", {
        ...(dateFilter
          ? { filterDate: dateFilter }
          : { startDate: twoDaysAgo.toISOString(), endDate: today.toISOString() }),
      });

      if (response.data.success) {
        const formattedAlerts = response.data.data.map((item, index) => {
          const imageUrl = item.imageUrl || null;
          const imgUrls  = imageUrl ? [{ url: imageUrl, time: item.timestamp, count: item.details?.total_persons }] : [];
          const alertTime = item.timestamp || item.updatedAt?.$date || item.updatedAt || null;

          let peakPersonCount = 0;
          if (item.zoneType === "Head Count" && item.details?.total_persons)
            peakPersonCount = item.details.total_persons;

          let personBreakdown = null;
          if (item.zoneType === "Head Count" && item.details) {
            personBreakdown = {
              kitchen: item.details.kitchen_persons || 0,
              sofa1:   item.details.sofa1_persons   || 0,
              sofa2:   item.details.sofa2_persons   || 0,
              sofa3:   item.details.sofa3_persons   || 0,
              total:   item.details.total_persons   || 0,
            };
          }

          return {
            id:            item._id?.$oid || item._id || index,
            time:          isoToTime(alertTime),
            date:          isoToDate(alertTime),
            timestamp:     alertTime,
            cam:           item.cameraName,
            location:      item.location,
            module:        item.zoneType,
            eventType:     item.eventType,
            level:         deriveLevel(item),
            peakPersonCount,
            personBreakdown,
            tailgatingDetails: item.zoneType === "Tailgating" ? item.details : null,
            intrusionDetails:  item.zoneType === "Intrusion"  ? item.details : null,
            cameraStatus:  item.cameraStatus,
            imgUrls,
            hasImages:     imgUrls.length > 0,
            uniqueEventId: item.uniqueEventId,
          };
        });

        const sorted = formattedAlerts.sort((a, b) => {
          const parseTs = (ts) => {
            if (!ts) return 0;
            if (typeof ts === "object" && ts.$date) return new Date(ts.$date).getTime();
            return new Date(ts).getTime();
          };
          return parseTs(b.timestamp) - parseTs(a.timestamp);
        });
        setAlerts(sorted);
      }
    } catch (error) {
      console.error("Error fetching alerts:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTablespace(); }, [dateFilter]);

  const formatEventId = (eventId) => {
    const match = eventId.match(/(.*)_(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})/);

    if (!match) return eventId;

    const [, prefix, date, time] = match;
    const [year, month, day] = date.split("-");

    return `${prefix} | ${day}/${month}/${year} ${time}`;
  };

  useEffect(() => {
    if (!previewAlert) { setImageLoading(false); return; }
    const current = previewAlert.imgUrls?.[previewAlert.currentIndex];
    if (!current?.url) { setImageLoading(false); return; }
    setImageLoading(true);
    const img = new Image();
    img.onload  = () => setImageLoading(false);
    img.onerror = () => setImageLoading(false);
    img.src = current.url;
  }, [previewAlert]);

  const filtered = alerts.filter((a) => {
    if (levelFilter  !== "all" && a.level  !== levelFilter)  return false;
    if (camFilter    !== "All" && a.cam    !== camFilter)    return false;
    if (moduleFilter !== "All" && a.module !== moduleFilter) return false;
    return true;
  });

  const critCount = alerts.filter((a) => a.level === "critical").length;
  const warnCount = alerts.filter((a) => a.level === "warning").length;
  const hasActiveFilter = levelFilter !== "all" || camFilter !== "All" || moduleFilter !== "All" || dateFilter !== "";
  const uniqueCameras = [...new Set(alerts.map((a) => a.cam))];
  const uniqueModules = [...new Set(alerts.map((a) => a.module))];

  const openPreview = (a, e) => {
    e?.stopPropagation();
    setPreviewAlert({ imgUrls: a.imgUrls, cam: a.cam, location: a.location, module: a.module, currentIndex: 0 });
  };

  return (
    <div className="flex flex-col gap-6 p-6 bg-slate-50 min-h-screen">

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <span className="text-[10px] font-bold uppercase tracking-widest text-red-500">
            Live + Historical
          </span>
          <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-slate-900">
            Alert Centre
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Security events across monitored zones — Intrusion, Tailgating &amp; Occupancy
          </p>
        </div>

        <div className="flex gap-2 flex-shrink-0">
          {critCount > 0 && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-center min-w-[80px]">
              <p className="text-2xl font-extrabold leading-none text-red-500">{critCount}</p>
              <p className="mt-1 text-[9px] font-bold uppercase tracking-wide text-red-400">Critical</p>
            </div>
          )}
          {warnCount > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-center min-w-[80px]">
              <p className="text-2xl font-extrabold leading-none text-amber-500">{warnCount}</p>
              <p className="mt-1 text-[9px] font-bold uppercase tracking-wide text-amber-400">Warning</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm px-5 py-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1.5">
            <Label>Severity</Label>
            <Select value={levelFilter} onChange={setLevelFilter} style={{ width: 148 }} size="middle">
              <Option value="all">All Severities</Option>
              <Option value="critical"><span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-red-500 inline-block" />Critical</span></Option>
              <Option value="warning"><span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-amber-500 inline-block" />Warning</span></Option>
              <Option value="ok"><span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-emerald-500 inline-block" />OK</span></Option>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Camera</Label>
            <Select value={camFilter} onChange={setCamFilter} style={{ width: 176 }} size="middle">
              <Option value="All">All Cameras</Option>
              {uniqueCameras.map((c) => <Option key={c} value={c}>{c}</Option>)}
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Zone Type</Label>
            <Select value={moduleFilter} onChange={setModuleFilter} style={{ width: 176 }} size="middle">
              <Option value="All">All Zone Types</Option>
              {uniqueModules.map((m) => <Option key={m} value={m}>{m}</Option>)}
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Date</Label>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="h-8 rounded-md border border-slate-300 bg-white px-2.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-400"
            />
          </div>

          {hasActiveFilter && (
            <button
              onClick={() => { setLevelFilter("all"); setCamFilter("All"); setModuleFilter("All"); setDateFilter(""); }}
              className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-[7px] text-xs font-semibold text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
            >
              Clear filters
            </button>
          )}

          <div className="ml-auto rounded-lg bg-slate-100 px-3 py-[7px] text-xs font-bold text-slate-600">
            {filtered.length} / {alerts.length} alerts
          </div>
        </div>
      </div>

      {/* ── Column Header ── */}
      {!loading && filtered.length > 0 && (
        <div
          className="hidden md:grid items-center gap-3 px-4 text-[10px] font-bold uppercase tracking-widest text-slate-400"
          style={{ gridTemplateColumns: "32px 72px 72px 1fr 100px 100px 1fr 80px 20px" }}
        >
          <span />
          <span>Time</span>
          <span>Snap</span>
          <span>Camera</span>
          <span>Zone</span>
          <span>Persons</span>
          <span>Location</span>
          <span className="text-right">Severity</span>
          <span />
        </div>
      )}

      {/* ── Alerts List ── */}
      <div className="flex flex-col gap-2">
        {loading && (
          <div className="py-12 text-center text-sm text-slate-500 font-medium">
            Loading alerts…
          </div>
        )}

        {!loading && filtered.map((a) => {
          const cfg    = levelCfg[a.level];
          const isOpen = expanded === a.id;

          return (
            <div
              key={a.id}
              className={`overflow-hidden rounded-xl border bg-white shadow-sm transition-all duration-150 ${cfg.border}`}
            >
              {/* ── Severity left bar ── */}
              <div className="flex">
                <div className={`w-1 flex-shrink-0 rounded-l-xl ${cfg.leftBar}`} />

                <div className="flex-1 min-w-0">
                  {/* ── Main Row ── */}
                  <div
                    className="grid items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-slate-50/80 transition-colors"
                    style={{ gridTemplateColumns: "32px 72px 72px 1fr 100px 100px 1fr 80px 20px" }}
                    onClick={() => setExpanded(isOpen ? null : a.id)}
                  >

                    {/* 1 — Severity icon */}
                    <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${cfg.bg}`}>
                      <cfg.Icon size={14} className={cfg.text} />
                    </div>

                    {/* 2 — Time */}
                    <div className="min-w-0">
                      <p className="font-mono text-[11px] font-bold text-slate-700 leading-tight">{a.time}</p>
                      <p className="text-[10px] text-slate-400 leading-tight mt-0.5">{a.date}</p>
                    </div>

                    {/* 3 — Thumbnail */}
                    {a.hasImages ? (
                      <AlertThumbnail
                        url={a.imgUrls[0].url}
                        cam={a.cam}
                        onClick={(e) => openPreview(a, e)}
                      />
                    ) : (
                      <NoImagePlaceholder />
                    )}

                    {/* 4 — Camera */}
                    <div className="flex items-center gap-1.5 min-w-0">
                      <ShieldAlert size={11} className="flex-shrink-0 text-slate-400" />
                      <span className="text-[11px] font-semibold text-slate-700 truncate">{a.cam}</span>
                    </div>

                    {/* 5 — Zone Type */}
                    <div>
                      <span className={`inline-block rounded-md px-2 py-0.5 text-[10px] font-bold leading-tight ${
                        a.module === "Intrusion"  ? "bg-red-50 text-red-600 border border-red-200"   :
                        a.module === "Tailgating" ? "bg-amber-50 text-amber-600 border border-amber-200" :
                        "bg-blue-50 text-blue-600 border border-blue-200"
                      }`}>
                        {a.module}
                      </span>
                    </div>

                    {/* 6 — Person Count */}
                    <div>
                      {a.module === "Head Count" && a.peakPersonCount > 0 ? (
                        <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 border border-blue-200 px-2 py-0.5 text-[10px] font-bold text-blue-600">
                          <Users size={9} />
                          {a.peakPersonCount} persons
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-300">—</span>
                      )}
                    </div>

                    {/* 7 — Location */}
                    <span className="truncate text-[12px] text-slate-600 min-w-0">
                      {a.location}
                    </span>

                    {/* 8 — Severity pill */}
                    <div className="flex justify-end">
                      <span className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-bold ${cfg.pillBg} ${cfg.text}`}>
                        {cfg.label}
                      </span>
                    </div>

                    {/* 9 — Chevron */}
                    <ChevronDown
                      size={14}
                      className={`text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-180" : "rotate-0"}`}
                    />
                  </div>

                  {/* ── Expanded Section ── */}
                  {isOpen && (
                    <div className="border-t border-slate-100 bg-slate-50/70 px-4 py-4 flex flex-col gap-5">

                      {/* Meta grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
                        {[
                          { label: "Camera",     value: a.cam },
                          { label: "Location",   value: a.location },
                          { label: "Zone Type",  value: a.module },
                          { label: "Event Type", value: a.eventType || a.module },
                          { label: "Severity",   value: cfg.label, className: cfg.text },
                          { label: "Timestamp",  value: `${a.time} · ${a.date}`, icon: <Clock size={11} className="text-slate-400 flex-shrink-0" /> },
                        ].map(({ label, value, className, icon }) => (
                          <div key={label}>
                            <Label>{label}</Label>
                            <div className="mt-1 flex items-center gap-1">
                              {icon}
                              <p className={`text-[12px] font-semibold text-slate-800 ${className || ""}`}>{value}</p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Head Count breakdown */}
                      {a.module === "Head Count" && a.personBreakdown && (
                        <div>
                          <Label>Occupancy Breakdown</Label>
                          <div className="mt-2 grid grid-cols-5 gap-2">
                            {[
                              { label: "Total",   value: a.personBreakdown.total,   highlight: true },
                              { label: "Kitchen", value: a.personBreakdown.kitchen },
                              { label: "Sofa 1",  value: a.personBreakdown.sofa1 },
                              { label: "Sofa 2",  value: a.personBreakdown.sofa2 },
                              { label: "Sofa 3",  value: a.personBreakdown.sofa3 },
                            ].map(({ label, value, highlight }) => (
                              <div key={label} className="rounded-lg bg-white p-3 border border-slate-200 text-center">
                                <p className="text-[10px] text-slate-400 mb-1">{label}</p>
                                <p className={`text-lg font-bold ${highlight ? "text-blue-600" : "text-slate-700"}`}>{value}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Event ID */}
                      {a.uniqueEventId && (
                        <div>
                          <Label>Event ID</Label>
                            <p className="mt-1 text-[11px] font-mono text-slate-500 break-all">
                              {formatEventId(a.uniqueEventId)}
                            </p>
                        </div>
                      )}

                      {/* Expanded image area */}
                      {a.hasImages && (
                        <div>
                          <Label>Captured Snapshot</Label>
                          <div className="mt-2 flex items-start gap-3">
                            <button
                              onClick={(e) => openPreview(a, e)}
                              className="group relative overflow-hidden rounded-xl border-2 border-slate-200 bg-slate-100 transition-all hover:border-violet-400 hover:shadow-lg"
                              style={{ width: 160, height: 112, flexShrink: 0 }}
                            >
                              <img
                                src={a.imgUrls[0].url}
                                alt="Snapshot preview"
                                className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                                onError={(e) => { e.target.style.display = "none"; }}
                              />
                              <div className="absolute inset-0 flex items-center justify-center bg-violet-900/0 transition-all group-hover:bg-violet-900/35">
                                <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Eye size={14} className="text-white" />
                                  <span className="text-xs font-bold text-white drop-shadow">Full view</span>
                                </div>
                              </div>
                            </button>
                            <div className="flex flex-col gap-1 justify-center py-1">
                              <p className="text-xs font-semibold text-slate-600">Security snapshot available</p>
                              <p className="text-[11px] text-slate-400">Click thumbnail to open full-size view</p>
                              <button
                                onClick={(e) => openPreview(a, e)}
                                className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-[11px] font-bold text-violet-600 transition hover:bg-violet-100 w-fit"
                              >
                                <Eye size={11} />
                                Open full size
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {!loading && filtered.length === 0 && (
          <div className="py-16 text-center bg-white rounded-xl border border-slate-200">
            <CheckCircle2 size={40} className="mx-auto mb-3 text-slate-300" />
            <p className="font-semibold text-slate-600">No alerts match the current filters</p>
            <p className="text-sm text-slate-400 mt-1">Try adjusting your filter criteria</p>
          </div>
        )}
      </div>

      {/* ── Image Preview Modal ── */}
      <Modal
        open={!!previewAlert}
        onCancel={() => { setPreviewAlert(null); setImageLoading(false); }}
        footer={null}
        centered
        title={
          previewAlert && (
            <div className="flex items-center gap-2">
              <ShieldAlert size={15} className="text-violet-500" />
              <span className="text-sm font-bold text-slate-800">{previewAlert.cam}</span>
              <span className="text-slate-300">·</span>
              <span className="text-sm text-slate-500">{previewAlert.location}</span>
              <span className={`ml-1 rounded px-2 py-0.5 text-[10px] font-bold ${
                previewAlert.module === "Intrusion"  ? "bg-red-50 text-red-600"   :
                previewAlert.module === "Tailgating" ? "bg-amber-50 text-amber-600" :
                "bg-blue-50 text-blue-600"
              }`}>{previewAlert.module}</span>
            </div>
          )
        }
        width={820}
        styles={{ body: { padding: "20px" } }}
      >
        {previewAlert && (() => {
          const current = previewAlert.imgUrls[previewAlert.currentIndex];
          return (
            <div className="flex flex-col gap-4">
              <div className="relative min-h-[420px] bg-slate-100 rounded-xl overflow-hidden">
                {imageLoading && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-50">
                    <div className="flex flex-col items-center gap-3">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-violet-500" />
                      <span className="text-sm text-slate-500">Loading snapshot…</span>
                    </div>
                  </div>
                )}
                <img
                  key={current.url}
                  src={current.url}
                  alt={`Security snapshot from ${previewAlert.cam}`}
                  className="w-full h-auto rounded-xl"
                  style={{ display: imageLoading ? "none" : "block" }}
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
                <div className="hidden items-center justify-center h-96 rounded-xl border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-400">
                  <div className="text-center">
                    <ImageOff size={32} className="mx-auto mb-2 text-slate-300" />
                    <p className="font-medium text-slate-500">Image failed to load</p>
                    <p className="text-xs mt-1 text-slate-400">{current.url?.substring(0, 60)}…</p>
                  </div>
                </div>
              </div>

              {current.time && (
                <div className="flex items-center justify-between text-xs text-slate-400 px-1">
                  <span className="flex items-center gap-1.5">
                    <Clock size={11} />
                    Captured: {isoToTime(current.time)}
                  </span>
                  {current.count && (
                    <span className="flex items-center gap-1.5">
                      <Users size={11} />
                      {current.count} persons detected
                    </span>
                  )}
                </div>
              )}

              {previewAlert.imgUrls.length > 1 && (
                <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
                  <button
                    disabled={previewAlert.currentIndex === 0}
                    onClick={() => setPreviewAlert((p) => ({ ...p, currentIndex: p.currentIndex - 1 }))}
                    className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition"
                  >
                    ← Previous
                  </button>
                  <span className="text-xs font-medium text-slate-400">
                    Frame {previewAlert.currentIndex + 1} of {previewAlert.imgUrls.length}
                  </span>
                  <button
                    disabled={previewAlert.currentIndex === previewAlert.imgUrls.length - 1}
                    onClick={() => setPreviewAlert((p) => ({ ...p, currentIndex: p.currentIndex + 1 }))}
                    className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition"
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default Alerts;